import { Injectable } from 'framework/di';
import { Signal, signal } from 'framework/store/signal';

import { DomHelper } from '../../helpers/dom.helper';
import { appIntegration } from '../../integration';
import { handleProductSwatches, quickAddToCart } from '../../product-item';
import { quickView } from '../../product-item/quick-view';
import { handleTooltip } from '../../tooltip';
import { safeParseJSON } from '../../utils';
import { AppService } from '../app/app.service';
import { CustomizationCompatibility } from '../backward-compatibility/customization.compatibility';
import { CartController } from '../cart/cart.controller';
import { CartService } from '../cart/cart.service';
import {
  AppTranslation,
  ExtendedRecommendationModelProperties,
  ExtendedRecommendationProduct,
  RecommendationModel,
} from './recommendation.model';
import { RecommendationService } from './recommendation.service';

/**
 * Controller for managing recommendation widget lifecycle, rendering, and carousel functionality.
 *
 * This controller orchestrates the complete lifecycle of recommendation widgets, from data fetching
 * to rendering and carousel initialization. It provides numerous extension points for Technical
 * Support teams to customize widget behavior for specific shops without modifying core code.
 *
 * @remarks
 * Key responsibilities include:
 * - Lazy loading widgets when they enter the viewport for performance
 * - Fetching recommendation data from the API
 * - Managing widget templates and rendering
 * - Configuring and initializing Slick carousel for carousel layouts
 * - Handling empty recommendation states
 *
 * Performance considerations:
 * - Uses IntersectionObserver for viewport detection to avoid unnecessary renders
 * - Lazy loads jQuery and Slick carousel only when carousel layout is needed
 * - Unobserves elements after rendering to prevent memory leaks
 *
 * @example
 * Extend to customize recommendation fetching with additional filters:
 * ```javascript
 * window.boostWidgetIntegration.extend('RecommendationWidgetController', (Base) => {
 *   return class CustomRecommendationController extends Base {
 *     protected async fetchRecommendationData(widgetId) {
 *       const model = await super.fetchRecommendationData(widgetId);
 *       if (model && this.customer?.tags?.includes('vip')) {
 *         // Filter products for VIP customers
 *         model.properties.products = model.properties.products.filter(
 *           p => p.tags?.includes('vip-eligible')
 *         );
 *       }
 *       return model;
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Extend to customize carousel configuration for specific shops:
 * ```javascript
 * window.boostWidgetIntegration.extend('RecommendationWidgetController', (Base) => {
 *   return class extends Base {
 *     protected getCarouselConfig(model) {
 *       const config = super.getCarouselConfig(model);
 *       // Customize for shop theme
 *       return {
 *         ...config,
 *         autoplay: true,
 *         autoplaySpeed: 5000,
 *         dots: false
 *       };
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class RecommendationWidgetController {
  private static readonly DROPDOWN_INDEX_ATTRIBUTE = 'boost_sd__dropdown-index';

  protected template?: string;
  protected readonly MOBILE_BREAKPOINT: number = 768;
  protected openingBundleDropdownIdx = signal(0);

  // Event listeners for cleanup
  protected readonly resizeHandler = () => {
    this.calcDropdownPosition();
  };
  protected readonly scrollHandler = () => {
    this.calcDropdownPosition();
  };
  protected readonly clickHandler = (e: MouseEvent) => {
    this.handleClickOutsideBundleDropdown(e);
  };

  /**
   * Reactive state management for the recommendation widget.
   *
   * Tracks loading status and the current recommendation model. The state
   * is observable and can be monitored for changes during the widget lifecycle.
   *
   * @remarks
   * This signal-based state management enables reactive UI updates and
   * is particularly useful when extending the controller to add custom
   * loading indicators or state-dependent behavior.
   */
  state: Signal<RecommendationWidgetControllerState>;

  /**
   * Reactive signal containing the widget's initialization properties.
   *
   * This signal is populated when the widget connects via the `connect()` method
   * and provides reactive access to the widget ID and other initialization props.
   * Useful for accessing widget configuration in extended methods.
   *
   * @remarks
   * The optional nature allows the controller to exist before connection.
   * Always check for existence before accessing in extension code.
   *
   * @example
   * Access widget ID in custom methods:
   * ```typescript
   * protected customMethod() {
   *   const widgetId = this.props?.value.widgetId;
   *   if (widgetId) {
   *     console.log(`Processing widget: ${widgetId}`);
   *   }
   * }
   * ```
   */
  props?: Signal<RecommendationWidgetControllerProps>;

  /**
   * Creates an instance of RecommendationWidgetController.
   *
   * @param domHelpers - Utility service for DOM manipulation and viewport observation
   * @param recommendationService - Service for fetching recommendation data and templates
   * @param appService - Core application service providing template rendering and configuration
   */
  constructor(
    protected domHelpers: DomHelper,
    protected recommendationService: RecommendationService,
    protected appService: AppService,
    protected customizationCompatibility: CustomizationCompatibility,
    protected cartService: CartService,
    protected cartController: CartController
  ) {
    this.state = signal({
      loading: false,
      model: null,
    });

    // Add event listeners with cleanup tracking
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('scroll', this.scrollHandler);
    document.addEventListener('click', this.clickHandler);
  }

  /**
   * Cleans up event listeners and resources when the controller is no longer needed.
   * This method should be called when the widget is removed or the component is destroyed
   * to prevent memory leaks.
   *
   * @remarks
   * This method removes global event listeners added in the constructor.
   * It should be called in component cleanup lifecycle hooks or when widgets are dynamically removed.
   *
   * @example
   * Clean up when removing widget:
   * ```typescript
   * // In component cleanup or widget removal
   * controller.destroy();
   * ```
   */
  public destroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('scroll', this.scrollHandler);
    document.removeEventListener('click', this.clickHandler);
  }

  /**
   * Generates the CSS selector for open dropdown elements.
   *
   * This method creates a CSS selector that targets dropdown elements in their opened state.
   * Override this method to customize dropdown targeting or add additional CSS classes.
   *
   * @param widgetId - The unique identifier of the widget used to create specific selectors
   * @returns CSS selector string targeting open dropdown elements for the specified widget
   *
   * @example
   * Override to add custom CSS classes for shop-specific styling:
   * ```typescript
   * public getDropdownOpenNodeKey(widgetId: string): string {
   *   const baseSelector = super.getDropdownOpenNodeKey(widgetId);
   *   return `${baseSelector}.custom-shop-dropdown-open`;
   * }
   * ```
   */
  public getDropdownOpenNodeKey(widgetId: string): string {
    return `[id="boost-sd__dropdown-list-${widgetId}"].boost-sd__dropdown-option-list--open`;
  }

  /**
   * Generates the CSS selector for dropdown elements.
   *
   * Creates a CSS selector that targets dropdown container elements for variant selection.
   * Override this method to customize dropdown element targeting or modify the selector structure.
   *
   * @param widgetId - The unique identifier of the widget used in the selector ID
   * @returns CSS selector string targeting dropdown container elements for the specified widget
   *
   * @example
   * Override to use custom dropdown ID format:
   * ```typescript
   * public getDropdownNodeKey(widgetId: string): string {
   *   return `[data-widget-dropdown="${widgetId}"]`;
   * }
   * ```
   */
  public getDropdownNodeKey(widgetId: string): string {
    return `[id="boost-sd__dropdown-${widgetId}"]`;
  }

  /**
   * Formats a price value according to shop settings and theme configuration.
   *
   * Applies shop-specific currency formatting including decimal handling, superscript cents,
   * and currency symbols. Override this method to implement custom price formatting logic,
   * add discounts, or modify display for specific customer segments.
   *
   * @param price - The price value in cents to format
   * @returns Formatted price string with currency symbol and shop-specific styling applied
   *
   * @example
   * Override to add VIP customer discounts:
   * ```typescript
   * public formatPrice(price: number): string {
   *   let adjustedPrice = price;
   *   if (this.customer?.tags?.includes('vip')) {
   *     adjustedPrice = price * 0.9; // 10% VIP discount
   *   }
   *   return super.formatPrice(adjustedPrice);
   * }
   * ```
   *
   * @example
   * Override to add custom currency formatting:
   * ```typescript
   * public formatPrice(price: number): string {
   *   const formatted = super.formatPrice(price);
   *   // Add custom formatting for specific regions
   *   return this.customer?.region === 'EU' ? `€${formatted}` : formatted;
   * }
   * ```
   */
  public formatPrice(price: number): string {
    const taeConfig = this.appService.TAEAppConfig;

    const priceSettings =
      taeConfig.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};

    const showCentAsSuperscript = priceSettings.showCentAsSuperscript;
    const removePriceDecimal = Boolean(
      (priceSettings as Record<string, unknown>).removePriceDecimal
    );

    const formatString = taeConfig.shop.money_format || '${{amount}}';

    const formattedResult = this.appService.formatCurrency(price, {
      format: formatString,
      showCentAsSuperscript: showCentAsSuperscript,
      removePriceDecimal: removePriceDecimal,
    });

    return formattedResult || '';
  }

  /**
   * Calculates the total price of checked products in a bundle.
   *
   * Sums the prices of all selected products in the recommendation bundle.
   * Override this method to apply bundle discounts, tax calculations, or custom
   * pricing logic for specific shops or customer segments.
   *
   * @param products - Array of recommendation products with selection state and pricing data
   * @returns Total price in cents of all checked products, before any additional processing
   *
   * @example
   * Override to apply bundle discounts:
   * ```typescript
   * public calcTotalPrice(products: ExtendedRecommendationProduct[]): number {
   *   const baseTotal = super.calcTotalPrice(products);
   *   const checkedCount = products.filter(p => p.checked).length;
   *
   *   // Apply bundle discount for 3+ items
   *   if (checkedCount >= 3) {
   *     return Math.round(baseTotal * 0.85); // 15% bundle discount
   *   }
   *   return baseTotal;
   * }
   * ```
   *
   * @example
   * Override to add tax calculation:
   * ```typescript
   * public calcTotalPrice(products: ExtendedRecommendationProduct[]): number {
   *   const subtotal = super.calcTotalPrice(products);
   *   const taxRate = this.getTaxRateForCustomer();
   *   return Math.round(subtotal * (1 + taxRate));
   * }
   * ```
   */
  public calcTotalPrice(products: ExtendedRecommendationProduct[]): number {
    let totalPrice = 0;
    for (const product of products) {
      if (product.checked) {
        totalPrice += product.selectedOption.originPrice;
      }
    }
    return totalPrice;
  }

  /**
   * Builds CSS style string for dropdown positioning.
   *
   * Creates inline styles for absolute positioning of dropdown elements relative to their
   * trigger buttons. Override this method to customize dropdown positioning logic,
   * add viewport edge detection, or implement custom positioning strategies.
   *
   * @param rootPosition - DOMRect containing position and dimensions of the dropdown trigger element
   * @returns CSS style string with position, dimensions, and z-index for dropdown positioning
   *
   * @example
   * Override to add viewport edge detection:
   * ```typescript
   * public buildDropboxStyle(rootPosition: DOMRect): string {
   *   const { width, height, left, top } = rootPosition;
   *   const dropdownHeight = 200; // Estimated dropdown height
   *
   *   // Check if dropdown would overflow viewport bottom
   *   const spaceBelow = window.innerHeight - (top + height);
   *   const shouldOpenUpward = spaceBelow < dropdownHeight;
   *
   *   const topPosition = shouldOpenUpward ? top - dropdownHeight : top + height;
   *
   *   return `position:fixed;z-index:1000;width:${width}px;left:${left}px;top:${topPosition}px`;
   * }
   * ```
   */
  public buildDropboxStyle(rootPosition: DOMRect): string {
    const { width, height, left, top } = rootPosition;
    return `position:fixed;z-index:2;width:${width}px;left:${left}px;top:${top + height}px`;
  }

  /**
   * Calculates and updates the position of open dropdowns based on their trigger elements.
   *
   * Dynamically repositions open dropdown elements when the page is scrolled or resized
   * to maintain proper alignment with their trigger buttons. Override this method to
   * implement custom positioning logic, add animations, or modify responsive behavior.
   *
   * @remarks
   * This method is called automatically on scroll and resize events. It updates the
   * inline styles of open dropdown elements to keep them properly positioned.
   *
   * @example
   * Override to add smooth positioning animations:
   * ```typescript
   * public calcDropdownPosition(): void {
   *   super.calcDropdownPosition();
   *
   *   // Add smooth transition for position changes
   *   const openDropdowns = document.querySelectorAll('.boost-sd__dropdown-option-list--open');
   *   openDropdowns.forEach(dropdown => {
   *     (dropdown as HTMLElement).style.transition = 'all 0.2s ease-in-out';
   *   });
   * }
   * ```
   */
  public calcDropdownPosition(): void {
    const widgetId = this.props?.value.widgetId;
    if (!widgetId) return;

    const openedDropdown = document.body.querySelector(this.getDropdownOpenNodeKey(widgetId));
    if (!openedDropdown) return;

    const widgetElement = document.getElementById(widgetId);
    if (!widgetElement) return;

    const dropdownBoxs = widgetElement.querySelectorAll(
      `.boost-sd__recommendation-bundle-item-info-content ${this.getDropdownNodeKey(widgetId)}`
    );
    if (dropdownBoxs.length === 0) return;

    const index = +(
      openedDropdown.getAttribute(RecommendationWidgetController.DROPDOWN_INDEX_ATTRIBUTE) ?? '-1'
    );
    if (index < 0 || index >= dropdownBoxs.length) return;

    const dropdownElement = dropdownBoxs[index];
    if (dropdownElement) {
      openedDropdown.setAttribute(
        'style',
        this.buildDropboxStyle(dropdownElement.getBoundingClientRect())
      );
    }
  }

  /**
   * Handles click events outside bundle dropdowns to close them.
   *
   * Implements click-outside behavior for dropdown menus to provide intuitive UX.
   * When a click occurs outside the dropdown or its trigger, the dropdown closes.
   * Override this method to customize close behavior or add additional event handling.
   *
   * @param e - The mouse event from the document click listener containing target information
   *
   * @remarks
   * This method is automatically bound to document click events when the controller
   * is instantiated. It manages both single and multiple dropdown states.
   *
   * @example
   * Override to add analytics tracking:
   * ```typescript
   * public handleClickOutsideBundleDropdown(e: MouseEvent): void {
   *   const hadOpenDropdown = this.openingBundleDropdownIdx.value >= 0;
   *
   *   super.handleClickOutsideBundleDropdown(e);
   *
   *   if (hadOpenDropdown && this.openingBundleDropdownIdx.value < 0) {
   *     this.analytics.track('dropdown_closed_outside_click', {
   *       widgetId: this.props?.value.widgetId
   *     });
   *   }
   * }
   * ```
   */
  public handleClickOutsideBundleDropdown(e: MouseEvent): void {
    const widgetId = this.props?.value.widgetId;
    if (!widgetId) return;

    const openingBundleDropdownIdx = this.openingBundleDropdownIdx.value;
    if (openingBundleDropdownIdx < 0) return;

    const dropdownOpenNodeKey = this.getDropdownOpenNodeKey(widgetId);
    if (
      !e.target ||
      (!(e.target as Element).closest(dropdownOpenNodeKey) &&
        !(e.target as Element).closest(this.getDropdownNodeKey(widgetId)))
    ) {
      const openedDropdown = document.body.querySelector(dropdownOpenNodeKey);
      if (openedDropdown) {
        openedDropdown.removeAttribute(RecommendationWidgetController.DROPDOWN_INDEX_ATTRIBUTE);
        openedDropdown.classList.remove('boost-sd__dropdown-option-list--open');
        document.body.removeChild(openedDropdown);
        this.openingBundleDropdownIdx.value = -1;
      }
    } else {
      const openedDropdowns = document.body.querySelectorAll(dropdownOpenNodeKey);
      for (const openedDropdown of openedDropdowns) {
        const index = openedDropdown.getAttribute(
          RecommendationWidgetController.DROPDOWN_INDEX_ATTRIBUTE
        );
        if (index && openingBundleDropdownIdx !== +index) {
          openedDropdown.removeAttribute(RecommendationWidgetController.DROPDOWN_INDEX_ATTRIBUTE);
          openedDropdown.classList.remove('boost-sd__dropdown-option-list--open');
          document.body.removeChild(openedDropdown);
        }
      }
    }
  }

  /**
   * Updates the total price display in the bundle widget UI.
   *
   * Refreshes the visual display of the total bundle price and updates accessibility
   * attributes. Override this method to customize price display formatting, add
   * additional UI elements, or implement custom accessibility features.
   *
   * @param totalPrice - The calculated total price value in cents
   * @param container - The HTML element containing the widget and price display elements
   *
   * @remarks
   * This method automatically formats the price using the shop's currency settings
   * and updates both the visual display and ARIA labels for accessibility.
   *
   * @example
   * Override to add savings display:
   * ```typescript
   * public updateTotalPriceUI(totalPrice: number, container: HTMLElement): void {
   *   super.updateTotalPriceUI(totalPrice, container);
   *
   *   // Calculate and display savings
   *   const originalTotal = this.calcOriginalTotal();
   *   const savings = originalTotal - totalPrice;
   *
   *   if (savings > 0) {
   *     const savingsElement = container.querySelector('.bundle-savings');
   *     if (savingsElement) {
   *       savingsElement.textContent = `Save ${this.formatPrice(savings)}`;
   *     }
   *   }
   * }
   * ```
   */
  public updateTotalPriceUI(totalPrice: number, container: HTMLElement): void {
    const formattedPrice = this.formatPrice(totalPrice);
    const widgetId = this.props?.value.widgetId;
    if (!widgetId) return;

    const priceWrapper = container.querySelector(
      '.boost-sd__recommendation-bundle-total-price .boost-sd__format-currency'
    );

    if (priceWrapper) {
      const targetElement = priceWrapper.querySelector('span') ?? priceWrapper;
      targetElement.textContent = formattedPrice;

      const totalPriceContainer = targetElement.closest(
        '.boost-sd__recommendation-bundle-total-price'
      );
      if (totalPriceContainer) {
        totalPriceContainer.setAttribute('aria-label', ` ${formattedPrice}`);
      }
    }
  }
  /**
   * Initializes the recommendation widget and sets up viewport observation for lazy loading.
   *
   * This is the main entry point for widget initialization. It loads the widget template,
   * sets up an IntersectionObserver to detect when the widget enters the viewport, and
   * triggers rendering only when visible for optimal performance.
   *
   * @param props - Configuration properties for the widget
   * @param parent - The parent HTML element where the widget will be rendered
   *
   * @throws {Error} When template loading fails or parent element is invalid
   *
   * @remarks
   * The widget uses lazy loading strategy - it won't fetch data or render until
   * the parent element is visible in the viewport. This improves initial page load performance.
   *
   * @example
   * Basic usage:
   * ```typescript
   * const controller = container.resolve(RecommendationWidgetController);
   * const widgetElement = document.querySelector('#recommendation-widget');
   * await controller.connect({ widgetId: 'homepage-recommendations' }, widgetElement);
   * ```
   */
  async connect(props: RecommendationWidgetControllerProps, parent: HTMLElement) {
    this.props = signal(props);

    const { widgetId, renderImmediately } = props;

    const template = await this.loadTemplate();
    this.template = template;

    // For modals/drawers, render immediately without waiting for viewport intersection
    if (renderImmediately) {
      await this.handleWidgetInViewport(widgetId, parent, () => {});
      return;
    }

    const unobserve = this.domHelpers.observeElementInViewport(parent, async () => {
      await this.handleWidgetInViewport(widgetId, parent, unobserve);
    });
  }

  /**
   * Loads the widget template from the recommendation service.
   *
   * Override this method to customize template loading logic, such as loading
   * shop-specific templates, adding template preprocessing, or implementing
   * template caching strategies.
   *
   * @returns Promise resolving to the widget template HTML string
   *
   * @throws {Error} When template fetching fails or returns invalid data
   *
   * @example
   * Override to use custom template for specific shop:
   * ```typescript
   * protected async loadTemplate() {
   *   // Check if shop has custom template
   *   const customTemplate = await this.fetchCustomTemplate();
   *   if (customTemplate) {
   *     return customTemplate;
   *   }
   *   return super.loadTemplate();
   * }
   * ```
   */
  protected async loadTemplate(): Promise<string> {
    return this.recommendationService.getWidgetTemplate();
  }

  /**
   * Handles the complete widget lifecycle when it enters the viewport.
   *
   * This method orchestrates the entire widget rendering process: fetching data,
   * handling loading states, managing empty states, rendering the widget, and
   * cleaning up the observer. Override this method to customize the entire
   * widget lifecycle or add custom analytics, logging, or error handling.
   *
   * @param widgetId - The unique identifier of the widget to render
   * @param parent - The parent HTML element where the widget will be rendered
   * @param unobserve - Callback function to stop observing the element after rendering
   *
   * @throws {Error} When widget rendering fails due to network, template, or DOM errors
   *
   * @remarks
   * The method follows this lifecycle:
   * 1. Set loading state to true
   * 2. Fetch recommendation data
   * 3. Set loading state to false
   * 4. Handle empty recommendations (remove widget)
   * 5. Update state with model
   * 6. Render widget
   * 7. Execute post-render hooks
   * 8. Stop observing element
   *
   * Error handling: Errors during rendering will propagate to the caller.
   * Consider wrapping this method in a try-catch block when extending.
   *
   * @example
   * Override to add custom analytics tracking:
   * ```typescript
   * protected async handleWidgetInViewport(widgetId, parent, unobserve) {
   *   // Track widget impression
   *   this.analytics.trackImpression('recommendation_widget', { widgetId });
   *
   *   try {
   *     await super.handleWidgetInViewport(widgetId, parent, unobserve);
   *     this.analytics.trackSuccess('widget_rendered', { widgetId });
   *   } catch (error) {
   *     this.analytics.trackError('widget_failed', { widgetId, error });
   *     throw error;
   *   }
   * }
   * ```
   */
  protected async handleWidgetInViewport(
    widgetId: string,
    parent: HTMLElement,
    unobserve: () => void
  ): Promise<void> {
    const stateValue = this.state.value;
    stateValue.loading = true;

    const recommendationModel = await this.fetchRecommendationData(widgetId);
    stateValue.loading = false;

    this.state.value = {
      ...stateValue,
    };

    if (!recommendationModel || recommendationModel.properties.products.length === 0) {
      this.handleEmptyRecommendation(parent, unobserve);
      return;
    }

    stateValue.model = recommendationModel;

    this.state.value = {
      ...stateValue,
    };

    await this.renderWidget(recommendationModel, parent);

    this.afterWidgetRender();

    unobserve();
  }

  /**
   * Fetches recommendation data from the recommendation service.
   *
   * Override this method to add shop-specific data enrichment, modify API calls,
   * add caching strategies, or implement custom fallback logic when recommendations
   * are unavailable.
   *
   * @param widgetId - The unique identifier of the widget to fetch data for
   *
   * @returns Promise resolving to the recommendation model with widget configuration
   * and product data, or null if no recommendations are available or fetch fails
   *
   * @throws {Error} When API request fails or returns invalid data (caught internally, returns null)
   *
   * @example
   * Override to add product filtering based on customer segments:
   * ```typescript
   * protected async fetchRecommendationData(widgetId) {
   *   const model = await super.fetchRecommendationData(widgetId);
   *
   *   if (model && this.customer) {
   *     // Filter out products not available in customer's region
   *     model.properties.products = model.properties.products.filter(
   *       p => p.availableRegions?.includes(this.customer.region)
   *     );
   *   }
   *
   *   return model;
   * }
   * ```
   *
   * @example
   * Override to implement caching:
   * ```typescript
   * protected async fetchRecommendationData(widgetId) {
   *   const cached = this.cache.get(widgetId);
   *   if (cached && !this.cache.isExpired(widgetId)) {
   *     return cached;
   *   }
   *
   *   const model = await super.fetchRecommendationData(widgetId);
   *   if (model) {
   *     this.cache.set(widgetId, model, { ttl: 300 }); // 5 min cache
   *   }
   *   return model;
   * }
   * ```
   */
  protected async fetchRecommendationData(widgetId: string): Promise<RecommendationModel | null> {
    const skipValidation = this.props?.value.skipValidation;
    return (
      (await this.recommendationService.getRecommendationModel(widgetId, { skipValidation })) ||
      null
    );
  }

  /**
   * Handles the case when recommendation data is empty or has no products.
   *
   * By default, removes the widget element from the DOM and stops observing.
   * Override this method to customize empty state behavior, such as showing
   * a fallback message, displaying alternative content, or tracking empty states.
   *
   * @param parent - The parent HTML element containing the widget
   * @param unobserve - Callback function to stop observing the element
   *
   * @example
   * Override to show fallback content instead of removing:
   * ```typescript
   * protected handleEmptyRecommendation(parent, unobserve) {
   *   parent.innerHTML = `
   *     <div class="no-recommendations">
   *       <p>Check back soon for personalized recommendations!</p>
   *     </div>
   *   `;
   *   unobserve();
   * }
   * ```
   *
   * @example
   * Override to track empty states for analytics:
   * ```typescript
   * protected handleEmptyRecommendation(parent, unobserve) {
   *   this.analytics.track('empty_recommendations', {
   *     widgetId: this.currentWidgetId,
   *     customerId: this.customer?.id
   *   });
   *   super.handleEmptyRecommendation(parent, unobserve);
   * }
   * ```
   */
  protected handleEmptyRecommendation(parent: HTMLElement, unobserve: () => void): void {
    parent.remove();
    unobserve();
  }

  /**
   * Renders the widget based on the model's layout configuration.
   *
   * Currently handles carousel layout rendering. Override this method to add support
   * for custom layouts, modify rendering logic, or add pre/post-render transformations.
   *
   * @param model - The recommendation model containing widget configuration and product data
   * @param parent - The parent HTML element where the widget will be rendered
   *
   * @remarks
   * The default implementation only renders carousel layouts. For grid or other layouts,
   * the template rendering is handled elsewhere in the widget lifecycle.
   *
   * @example
   * Override to add support for custom grid layout:
   * ```typescript
   * protected async renderWidget(model, parent) {
   *   const layout = model.properties.widgetDesignSettings?.layoutDisplay;
   *
   *   if (layout === 'carousel') {
   *     await this.renderCarouselWidget(parent);
   *   } else if (layout === 'masonry') {
   *     await this.renderMasonryWidget(parent);
   *   } else {
   *     await this.renderGridWidget(parent);
   *   }
   * }
   * ```
   */
  protected async renderWidget(model: RecommendationModel, parent: HTMLElement): Promise<void> {
    const layout = model.properties.widgetDesignSettings?.layoutDisplay;
    if (layout === 'carousel') {
      await this.renderCarouselWidget(parent);
    } else if (layout === 'bundle') {
      await this.renderBundleWidget(parent);
    }
  }
  /**
   * Called after widget rendering is complete.
   *
   * This lifecycle hook is executed after the widget has been fully rendered in the DOM.
   * It triggers app integration for cart buttons, executes legacy customization hooks
   * for backward compatibility, and other interactive elements.
   *
   * Override this method to add post-render logic like analytics tracking, custom event
   * handlers, third-party integrations, or accessibility enhancements.
   *
   * @remarks
   * The method performs the following in order:
   * 1. Triggers app integration (cart buttons, quick view, etc.)
   * 2. Executes legacy customization.afterRender hooks for backward compatibility
   *    - Allows existing shops with custom JS to continue working
   *    - Supports both general and recommendation-specific hooks
   *
   * @example
   * Override to add analytics tracking:
   * ```typescript
   * protected afterWidgetRender() {
   *   super.afterWidgetRender(); // Important: call super to maintain integrations
   *
   *   const model = this.state.value.model;
   *   if (model) {
   *     this.analytics.trackProductImpressions(
   *       model.properties.products.map(p => ({
   *         id: p.id,
   *         name: p.title,
   *         list: `Recommendations - ${model.properties.widgetId}`
   *       }))
   *     );
   *   }
   * }
   * ```
   *
   * @example
   * Override to add custom interactive features:
   * ```typescript
   * protected afterWidgetRender() {
   *   super.afterWidgetRender();
   *
   *   // Add quick view functionality
   *   document.querySelectorAll('.product-quick-view').forEach(btn => {
   *     btn.addEventListener('click', (e) => this.handleQuickView(e));
   *   });
   * }
   * ```
   */
  protected afterWidgetRender(): void {
    appIntegration();

    const widgetId = this.props?.value.widgetId;
    if (widgetId) {
      const blockId = `boost-sd-widget-${widgetId}`;
      this.customizationCompatibility.afterRender(blockId, 'recommendation');

      // Apply product price formatting, swatches, URLs, and tooltips
      const widgetElement = document.getElementById(widgetId);
      if (widgetElement) {
        const context = this.buildLegacyContext(widgetElement);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handleProductSwatches(context as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handleTooltip(context as any);

        // Attach product interaction event handlers
        this.attachProductInteractionHandlers(widgetElement);
      }
    }
  }

  /**
   * Builds a legacy context object for compatibility with product-item helpers.
   *
   * This method creates a minimal Context object required by legacy helper functions.
   * The helper functions will extract additional settings from DOM attributes and
   * global configuration as needed.
   *
   * @param widgetElement - The widget DOM element
   * @returns Minimal context object with document and element references
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected buildLegacyContext(widgetElement: HTMLElement): Record<string, any> {
    const taeConfig = this.appService.TAEAppConfig;

    return {
      $element: widgetElement,
      document: document,
      blockType: 'recommendation',
      app: {
        ...taeConfig,
      },
    };
  }

  /**
   * Attaches click event handlers for product interactions (add to cart, quick view, select options).
   *
   * This method sets up event delegation for product item clicks within the recommendation widget.
   * Uses the same pattern as filter module for consistency.
   *
   * @param widgetElement - The widget container element
   */
  protected attachProductInteractionHandlers(widgetElement: HTMLElement): void {
    const handleClick = async (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Handle close button for quick add to cart popup
      if (target.closest('.boost-sd__popup-select-option-close-btn')) {
        this.handleCloseQuickAddToCart(target);
        return;
      }

      const metadataValue = target.dataset.metadata || target.getAttribute('metadata');

      if (!metadataValue) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let metadataObject: any = null;
      try {
        metadataObject = safeParseJSON(metadataValue);
      } catch (error) {
        this.appService.logger.error('Failed to parse product metadata:', {
          metadataValue,
          error,
        });
        return;
      }

      if (!metadataObject?.action) {
        return;
      }

      const action = metadataObject.action;
      // Get block context from appService like filter module does
      const block = this.appService.blockByType('recommendation');

      // Handle add to cart action
      if (action.addToCart) {
        event.preventDefault();
        await this.cartController.addToCart({ ...action.addToCart }, target);
      }

      // Handle quick view action
      if (action.quickView) {
        event.preventDefault();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        quickView(block as any, action.quickView, target);
      }

      // Handle quick add to cart action
      if (action.quickAddToCart) {
        event.preventDefault();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        quickAddToCart(block as any, action.quickAddToCart, target);
      }

      // Handle go to product detail action
      if (action.goToProductDetail) {
        this.handleGoToProductDetail(target);
      }
    };

    widgetElement.addEventListener('click', handleClick);
  }

  /**
   * Handles navigation to product detail page.
   * Builds product URL with variant and navigates to the product page.
   *
   * @param target - The clicked element
   */
  protected handleGoToProductDetail(target: HTMLElement): void {
    // Check if need to redirect or not, exclude wishlist buttons
    const tagName = target?.tagName?.toLowerCase();
    const closestTagWishlist = target?.closest('wishlist-button-collection');
    const noRedirect = target?.dataset?.noRedirect;
    const closestNoRedirect = target?.closest("[data-no-redirect='true']");

    if (
      tagName === 'wishlist-button-collection' ||
      closestTagWishlist ||
      noRedirect === 'true' ||
      closestNoRedirect
    ) {
      return;
    }

    const productItem = target.closest('.boost-sd__product-item');
    if (!productItem) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const productData = safeParseJSON(productItem.getAttribute('data-product') || '{}') as any;
    const { handle, splitProduct, variantId } = productData || {};

    const taeConfig = this.appService.TAEAppConfig;
    const { current_tags = [] } = taeConfig.generalSettings || {};

    // Build product URL with variant
    let productUrl = `/products/${handle}`;

    if (variantId && !splitProduct) {
      productUrl += `?variant=${variantId}`;
    }

    // Add collection parameter if enabled
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const addCollectionToProductUrl = (taeConfig.generalSettings as any)?.addCollectionToProductUrl;
    if (addCollectionToProductUrl && current_tags.length > 0) {
      const separator = productUrl.includes('?') ? '&' : '?';
      productUrl += `${separator}collection=${current_tags[0]}`;
    }

    window.location.href = productUrl;
  }

  /**
   * Handles closing the quick add to cart popup.
   * Hides the popup and returns focus to the select option button.
   *
   * @param target - The close button element that was clicked
   */
  protected handleCloseQuickAddToCart(target: HTMLElement): void {
    const popupSelection = target.closest('.boost-sd__popup-select-option') as HTMLElement;
    if (popupSelection) {
      popupSelection.style.display = 'none';
    }
    const productItem = target.closest('.boost-sd__product-item');
    const selectButton = productItem?.querySelector(
      '.boost-sd__button--select-option'
    ) as HTMLElement;
    if (selectButton) {
      selectButton.focus();
    }
  }

  /**
   * Gets the mobile breakpoint value in pixels.
   *
   * The breakpoint determines when the carousel switches from desktop to mobile
   * configuration. Default is 768px. Override this method to customize the mobile
   * breakpoint for shops with specific responsive design requirements.
   *
   * @returns The breakpoint width in pixels where mobile layout activates
   *
   * @example
   * Override to use custom breakpoint for shop theme:
   * ```typescript
   * protected getMobileBreakpoint() {
   *   // Match shop's theme breakpoint
   *   return 992; // Bootstrap's md breakpoint
   * }
   * ```
   */
  protected getMobileBreakpoint(): number {
    return this.MOBILE_BREAKPOINT;
  }

  /**
   * Gets the Slick carousel configuration for desktop view.
   *
   * Configures carousel behavior including slides to show/scroll, navigation arrows,
   * dots, and responsive breakpoints. The configuration respects RTL (right-to-left)
   * layout direction from the document. Override this method to customize carousel
   * behavior for specific shops or add features like autoplay.
   *
   * @param model - The recommendation model containing widget design settings
   *
   * @returns Slick carousel configuration object with desktop and responsive settings
   *
   * @remarks
   * The default configuration:
   * - Shows/scrolls number of products based on widget settings (default 4)
   * - Enables dots navigation, disables arrows
   * - Enables infinite scrolling and dragging
   * - Disables accessibility features for keyboard navigation (handled separately)
   * - Includes mobile breakpoint with different settings
   * - Respects RTL layout direction
   *
   * @example
   * Override to add autoplay and customize for shop:
   * ```typescript
   * protected getCarouselConfig(model) {
   *   const config = super.getCarouselConfig(model);
   *   return {
   *     ...config,
   *     autoplay: true,
   *     autoplaySpeed: 5000,
   *     pauseOnHover: true,
   *     arrows: true // Show navigation arrows
   *   };
   * }
   * ```
   *
   * @example
   * Override to customize slides for shop layout:
   * ```typescript
   * protected getCarouselConfig(model) {
   *   return {
   *     ...super.getCarouselConfig(model),
   *     slidesToShow: 3, // Always show 3 products
   *     slidesToScroll: 1, // Scroll one at a time
   *     centerMode: true,
   *     centerPadding: '60px'
   *   };
   * }
   * ```
   */
  protected getCarouselConfig(model: RecommendationModel): SlickOptions {
    const { widgetDesignSettings = {} } = model.properties;
    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';

    return {
      dots: true,
      infinite: true,
      slidesToShow: widgetDesignSettings.numberOfProductPerRow || 4,
      slidesToScroll: widgetDesignSettings.numberOfProductPerRow || 4,
      arrows: false,
      draggable: true,
      useTransform: false,
      focusOnSelect: false,
      focusOnChange: false,
      accessibility: false,
      responsive: [
        {
          breakpoint: this.getMobileBreakpoint(),
          settings: this.getMobileCarouselConfig(),
        },
      ],
      rtl: isRtl,
    };
  }

  /**
   * Gets the Slick carousel configuration for mobile view.
   *
   * Defines mobile-specific carousel behavior when viewport width is below the mobile
   * breakpoint. Override this method to customize mobile carousel experience for
   * specific shops or device requirements.
   *
   * @returns Partial Slick carousel configuration for mobile devices
   *
   * @remarks
   * The default mobile configuration:
   * - Shows 2 slides at a time
   * - Scrolls 2 slides per interaction
   * - Enables dots navigation
   * - Enables infinite scrolling and dragging
   * - Disables accessibility features (handled separately)
   *
   * @example
   * Override to show single slide on mobile:
   * ```typescript
   * protected getMobileCarouselConfig() {
   *   return {
   *     ...super.getMobileCarouselConfig(),
   *     slidesToShow: 1,
   *     slidesToScroll: 1,
   *     dots: false // Hide dots for single slide
   *   };
   * }
   * ```
   *
   * @example
   * Override to enable vertical scrolling on mobile:
   * ```typescript
   * protected getMobileCarouselConfig() {
   *   return {
   *     dots: false,
   *     vertical: true,
   *     verticalSwiping: true,
   *     slidesToShow: 2,
   *     slidesToScroll: 1
   *   };
   * }
   * ```
   */
  protected getMobileCarouselConfig(): Partial<SlickOptions> {
    return {
      dots: true,
      infinite: true,
      slidesToShow: 2,
      slidesToScroll: 2,
      arrows: false,
      draggable: true,
      focusOnSelect: false,
      focusOnChange: false,
      accessibility: false,
    };
  }

  /**
   * Initializes and configures the Slick carousel with event handlers and accessibility features.
   *
   * This method handles the complete carousel setup including:
   * - Initializing Slick carousel with configuration
   * - Setting up RTL (right-to-left) layout if needed
   * - Configuring keyboard navigation with proper tabindex management
   * - Attaching carousel navigation button handlers
   * - Managing focus states for accessibility
   *
   * @param model - The recommendation model containing widget configuration
   * @param container - The HTML element containing the carousel
   *
   * @remarks
   * The carousel implements custom accessibility features:
   * - Updates tabindex for slides based on visibility
   * - Sets aria-label for each slide indicating position
   * - Syncs focus after slide changes
   * - Manages focusable elements within slides
   *
   * Performance note: This method uses jQuery and Slick carousel which are
   * lazy-loaded only when carousel layout is needed.
   *
   * @example
   * Override to add custom carousel event tracking:
   * ```typescript
   * applyRecommendationCarouselEvent(model, container) {
   *   super.applyRecommendationCarouselEvent(model, container);
   *
   *   // Track carousel interactions
   *   const slider = window.$appJQuery('.boost-sd__slider-container');
   *   slider.on('afterChange', (event, slick, currentSlide) => {
   *     this.analytics.track('carousel_slide_change', {
   *       widgetId: model.properties.widgetId,
   *       slideIndex: currentSlide
   *     });
   *   });
   * }
   * ```
   */
  applyRecommendationCarouselEvent(model: RecommendationModel, container: HTMLElement) {
    const { widgetId } = model.properties;
    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
    // Get carousel configuration
    const carouselConfig = this.getCarouselConfig(model);

    // Init carousel
    const slider = (
      window.$appJQuery('.boost-sd__slider-container').not('.slick-initialized') as SlickInstance
    ).slick(carouselConfig);

    if (isRtl) {
      window.$appJQuery('.boost-sd__slider-container').css({
        direction: 'rtl',
      });

      window.$appJQuery('.boost-sd__slider-container .slick-slide').css({
        float: 'right',
      });
    }

    /**
     * updateTabindex() is the function to improve the behavior when navigate carousel by keyboard
     * Updates the tabindex and aria-label attributes of the slider container elements.
     *
     * Specifically, this function:
     * - Sets the aria-label attribute for each non-cloned slide to indicate its position (e.g. "Slide 1 of 5").
     * - Sets the tabindex attribute to 0 for the currently active slide and its focusable child elements.
     * - Sets the tabindex attribute to -1 for all other slides and their focusable child elements.
     *
     * This ensures that the slider is accessible and can be navigated using keyboard navigation.
     */
    const updateTabindex = () => {
      const items = container.querySelectorAll('.boost-sd__slider-container .slick-slide');
      const selectors = 'a[href], button:not([disabled]), .boost-sd__radio-label[tabindex]';
      const totalSlides = [...items].filter(
        (item) => !item.classList.contains('slick-cloned')
      ).length;
      let indexOfActiveSlide = 0;

      items.forEach((item) => {
        if (!(item instanceof HTMLElement)) return;
        if (!item.classList.contains('slick-cloned')) {
          indexOfActiveSlide++;
          item.setAttribute('aria-label', `Slide ${indexOfActiveSlide} of ${totalSlides}`);
        }

        const tabIndex = item.classList.contains('slick-active') ? 0 : -1;
        item.tabIndex = tabIndex;
        item.querySelectorAll(selectors).forEach((element) => {
          if (element instanceof HTMLElement) {
            element.tabIndex = tabIndex;
          }
        });
      });
    };

    const syncSlideFocusAfterChange = () => {
      updateTabindex();
      setTimeout(() => {
        window.$appJQuery(`#${widgetId} .slick-current`).trigger('focus');
      }, 400);
    };

    updateTabindex();

    slider.on('afterChange', syncSlideFocusAfterChange);

    this.attachCarouselButtonHandlers(container, slider);
  }

  /**
   * Attaches all event handlers for the 'bundle' layout (Dropdown, Checkbox, AddToCart, Image Link).
   * Logic moved and refactored from index.js/processRecommendationBundleEvent.
   *
   * @param model - The recommendation model
   * @param container - The widget's root HTML element
   */
  protected applyRecommendationBundleEvent(
    model: RecommendationModel,
    container: HTMLElement
  ): void {
    const widgetId = model.properties.widgetId;

    const products = model.properties.products as ExtendedRecommendationProduct[];

    const openDropdownClass = 'boost-sd__dropdown-option-list--open';
    const dropdownListSelector = `[id="boost-sd__dropdown-list-${widgetId}"]`;
    const checkedClass = 'boost-sd__recommendation-bundle-checkbox-box--checked';

    const LINK_BLUR_CLASS = 'boost-sd__recommendation-bundle-link-image--blur';
    const PRODUCT_BLUR_CLASS = 'boost-sd__product-image--blur';
    const INFO_CONTENT_BLUR_CLASS = 'boost-sd__recommendation-bundle-item-info-content--blur';

    // -----------------------
    // Helpers
    // -----------------------
    const closeAllOpenedDropdowns = (): void => {
      document.querySelectorAll(`.${openDropdownClass}`).forEach((el) => {
        el.classList.remove(openDropdownClass);
        try {
          el.parentElement === document.body && el.remove();
        } catch {
          // Ignore DOM removal errors - element may already be removed or not attached
        }
      });
    };

    const setBlurUIByIndex = (productIndex: number, isChecked: boolean): void => {
      // For style 1, we need to find elements by index across different containers
      // Image elements are in .boost-sd__recommendation-bundle-image-item
      // Info content elements are in .boost-sd__recommendation-bundle-item-info-content
      // Dropdowns are in .boost-sd__recommendation-bundle-item-info

      // Get image element for style 1/style 2
      const imageContainers = container.querySelectorAll(
        '.boost-sd__recommendation-bundle-image-item'
      );
      const imageElement = imageContainers[productIndex] as HTMLElement | null;

      // Get info content element (style 1: separate containers, style 2: within image container)
      let infoContentElement: Element | null = null;
      let dropdownElement: Element | null = null;

      // Try style 2 first (info content within image container)
      if (imageElement) {
        infoContentElement = imageElement.querySelector(
          '.boost-sd__recommendation-bundle-item-info-content'
        );
        dropdownElement = imageElement.querySelector('.boost-sd__dropdown');
      }

      // If not found, try style 1 (separate info containers)
      if (!infoContentElement) {
        const infoContainers = container.querySelectorAll(
          '.boost-sd__recommendation-bundle-item-info-content'
        );
        infoContentElement = infoContainers[productIndex] as HTMLElement | null;
      }

      if (!dropdownElement) {
        const dropdownContainers = container.querySelectorAll('.boost-sd__dropdown');
        dropdownElement = dropdownContainers[productIndex] as HTMLElement | null;
      }

      if (isChecked) {
        // Product is checked - remove blur (enable)
        // Handle image blur (using imageElement for style 1 and 2)
        if (imageElement) {
          imageElement
            .querySelector('.boost-sd__product-link-image')
            ?.classList.remove(LINK_BLUR_CLASS);
          imageElement
            .querySelector('.boost-sd__product-image')
            ?.classList.remove(PRODUCT_BLUR_CLASS);
        }

        // Handle info content blur
        if (infoContentElement) {
          infoContentElement.classList.remove(INFO_CONTENT_BLUR_CLASS);
        }

        // Handle dropdown state
        if (dropdownElement instanceof HTMLElement) {
          dropdownElement.classList.remove('boost-sd__dropdown--disabled');
          dropdownElement.setAttribute('tabindex', '0');
        }
      } else {
        // Product is unchecked - add blur (disable)
        // Handle image blur (using imageElement for style 1 and 2)
        if (imageElement) {
          imageElement
            .querySelector('.boost-sd__product-link-image')
            ?.classList.add(LINK_BLUR_CLASS);
          imageElement.querySelector('.boost-sd__product-image')?.classList.add(PRODUCT_BLUR_CLASS);
        }

        // Handle info content blur
        if (infoContentElement) {
          infoContentElement.classList.add(INFO_CONTENT_BLUR_CLASS);
        }

        // Handle dropdown state
        if (dropdownElement instanceof HTMLElement) {
          dropdownElement.classList.add('boost-sd__dropdown--disabled');
          dropdownElement.removeAttribute('tabindex');
        }

        closeAllOpenedDropdowns();
      }
    };

    const syncTotalStateAndUI = () => {
      const totalPrice = this.calcTotalPrice(products);
      const hasSelectedProduct = products.some((p) => p.checked);

      if (this.state.value.model) {
        this.state.value.model.properties.products = products;
        (this.state.value.model.properties as Record<string, unknown>).totalPrice =
          this.formatPrice(totalPrice);
        (this.state.value.model.properties as Record<string, unknown>).hasSelectedProduct =
          hasSelectedProduct;
      }
      this.state.value = { ...this.state.value };

      this.updateTotalPriceUI(totalPrice, container);
    };

    // 1) DROPDOWN (variant select)
    const dropdownHtmlElementsList = container.querySelectorAll(`.boost-sd__dropdown`);

    dropdownHtmlElementsList.forEach((dropdownRoot, dropdownIndex) => {
      if (!(dropdownRoot instanceof HTMLElement)) return;

      // Find the corresponding product by index
      const product = products[dropdownIndex];
      if (!product || product.variants.length === 0) return;

      const dropdownListElement = dropdownRoot.querySelector(dropdownListSelector);
      if (!dropdownListElement) return;

      dropdownRoot.addEventListener('click', (e) => {
        if (dropdownRoot.classList.contains('boost-sd__dropdown--disabled')) return;

        e.stopPropagation();

        const isOpen = dropdownListElement.classList.contains(openDropdownClass);
        closeAllOpenedDropdowns();

        if (!isOpen) {
          dropdownListElement.classList.add(openDropdownClass);
          dropdownListElement.setAttribute(
            RecommendationWidgetController.DROPDOWN_INDEX_ATTRIBUTE,
            String(dropdownIndex)
          );
          dropdownListElement.setAttribute(
            'style',
            this.buildDropboxStyle(dropdownRoot.getBoundingClientRect())
          );
          document.body.appendChild(dropdownListElement);
        }
      });

      // Get all dropdown option elements
      const allVariantOptionHtmlNodes = Array.from(
        dropdownListElement.querySelectorAll('.boost-sd__dropdown-option')
      );

      // Filter and bind click events to available variants
      allVariantOptionHtmlNodes.forEach((optionNode, optionIndex) => {
        if (!(optionNode instanceof HTMLElement)) return;

        // Check if variant exists at this index
        if (optionIndex >= product.variants.length) {
          optionNode.remove();
          return;
        }

        const variant = product.variants[optionIndex];
        if (!variant.available) {
          optionNode.remove();
          return;
        }

        optionNode.addEventListener('click', (e) => {
          e.stopPropagation();

          // close dropdown
          dropdownListElement.classList.remove(openDropdownClass);
          dropdownListElement.remove();

          // set selected option - fix price parsing to handle cents
          const priceInCents = Number(variant.price) || 0;
          const originPrice = priceInCents;
          const formattedPrice = this.formatPrice(originPrice);

          product.checked = true;
          product.selectedOption = {
            id: variant.id,
            title: variant.title,
            originPrice,
            price: formattedPrice,
          };

          // update dropdown selected text
          const selectedTextElement = dropdownRoot.querySelector(
            '.boost-sd__dropdown-selected-option .boost-sd__dropdown-option-text'
          );

          if (selectedTextElement instanceof HTMLElement) {
            selectedTextElement.textContent = variant.title;
            selectedTextElement.setAttribute('aria-label', `Current option: ${variant.title}`);
          }

          // update checkbox UI by finding checkbox in same product container
          const productContainer = dropdownRoot.closest(
            '.boost-sd__recommendation-bundle-image-item'
          );
          const checkboxHtmlNode = productContainer?.querySelector(
            '.boost-sd__recommendation-bundle-checkbox-box'
          ) as HTMLElement | null;

          if (checkboxHtmlNode) {
            checkboxHtmlNode.classList.add(checkedClass);
            checkboxHtmlNode.setAttribute('aria-checked', 'true');
            checkboxHtmlNode.setAttribute('aria-label', 'Click to remove from bundle');
          }

          // blur -> unblur using product index
          setBlurUIByIndex(dropdownIndex, true);

          // update item price + total
          this.updateItemPriceUI(dropdownRoot, formattedPrice);
          syncTotalStateAndUI();
        });
      });
    });

    document.addEventListener('click', () => {
      closeAllOpenedDropdowns();
    });

    // -----------------------
    // 2) CHECKBOX (toggle blur/unblur + disable dropdown)
    // -----------------------
    const checkboxElements = container.querySelectorAll(
      `.boost-sd__recommendation-bundle-checkbox-box`
    ) as NodeListOf<HTMLElement>;

    checkboxElements.forEach((checkboxNode, checkboxIndex) => {
      if (checkboxIndex >= products.length) return;
      const product = products[checkboxIndex];

      checkboxNode.addEventListener('click', (e) => {
        e.stopPropagation();

        const isChecked = checkboxNode.classList.contains(checkedClass);

        product.checked = !isChecked;

        checkboxNode.classList.toggle(checkedClass);
        checkboxNode.setAttribute('aria-checked', product.checked ? 'true' : 'false');
        checkboxNode.setAttribute(
          'aria-label',
          product.checked ? 'Click to remove from bundle' : 'Click to add to bundle'
        );

        setBlurUIByIndex(checkboxIndex, product.checked);
        syncTotalStateAndUI();
      });
    });

    // 3) ADD TO CART
    const addToCartButton = container.querySelector('.boost-sd__btn-add-to-cart');
    const ctaBtnText = addToCartButton?.querySelector('span') ?? addToCartButton;
    const textAddBundleAddToCart = ctaBtnText?.textContent ?? 'Add bundle to cart';

    addToCartButton?.addEventListener('click', async () => {
      const data = products
        .filter((p) => p.checked)
        .map((p) => ({
          id: String(p.selectedOption.id || ''),
          quantity: 1,
          sellingPlanId: null,
          properties: {},
        }))
        .filter((item) => item.id);

      if (!data.length) return;

      const translation = this.appService.TAEAppConfig.translation as AppTranslation;
      const addingText = translation.productItem?.atcAddingToCartBtnLabel ?? 'Adding...';
      const addedText = translation.productItem?.atcAddedToCartBtnLabel ?? 'Added!';
      const failedText = translation.productItem?.atcFailedToCartBtnLabel ?? 'Failed!';

      if (ctaBtnText) ctaBtnText.textContent = addingText;
      if (addToCartButton instanceof HTMLButtonElement) {
        addToCartButton.disabled = true;
      }

      try {
        const items = data.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          selling_plan: item.sellingPlanId,
          properties: item.properties,
        }));

        const result = await this.cartService.addToCart({ items });
        if (ctaBtnText) ctaBtnText.textContent = result.success ? addedText : failedText;
      } catch (err) {
        if (ctaBtnText) ctaBtnText.textContent = failedText;
        console.error('Add bundle to cart exception', err);
      }

      setTimeout(() => {
        if (ctaBtnText) ctaBtnText.textContent = textAddBundleAddToCart;
        if (addToCartButton instanceof HTMLButtonElement) {
          addToCartButton.disabled = false;
        }
      }, 500);
    });

    // 4) PRODUCT LINK NAVIGATION
    this.attachProductLinkHandlers(container, model);
  }

  /**
   * Attaches click event handlers to product links and images for navigation and tracking.
   * Handles both .boost-sd__product-link and .boost-sd__product-image elements.
   * Based on the original implementation logic for consistency.
   *
   * @param container - The widget container element
   * @param model - The recommendation model for analytics context
   */
  protected attachProductLinkHandlers(container: HTMLElement, model: RecommendationModel): void {
    // Handle product links (titles)
    const linkElements = container.querySelectorAll('.boost-sd__product-link');

    linkElements.forEach((link) => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');

        // Only handle navigation if href exists and is a valid product link
        if (href?.startsWith('/products/')) {
          // Track product click for analytics and session storage
          if (link instanceof HTMLAnchorElement) {
            this.trackProductLinkClick(link, model);
          }

          // Allow default navigation to proceed
          return;
        }

        // If no valid href, prevent the click
        e.preventDefault();
        console.warn('Product link clicked but no valid href found:', link);
      });
    });

    // Handle product images (replicate old bundle logic)
    const imageElements = container.querySelectorAll('.boost-sd__product-image');

    imageElements.forEach((image, index) => {
      if (!(image instanceof HTMLElement)) return;

      image.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault(); // Prevent any default behavior

        const products = model.properties.products;
        if (index >= products.length) return;

        const product = products[index];
        if (!product) return;

        // Build the product URL (simplified version)
        const productUrl = `/products/${product.handle}`;

        // Store selected variant/product ID (matches old SELECTED_PRODUCT_ID)
        const extendedProduct = product as ExtendedRecommendationProduct;
        const selectedVariant = extendedProduct.selectedOption.id;
        const firstVariant = product.variants[0];
        const variantId = selectedVariant || firstVariant?.id || product.id;

        try {
          sessionStorage.setItem('BOOST_SELECTED_PRODUCT_ID', String(variantId));
        } catch (error) {
          console.debug('Could not set sessionStorage:', error);
        }

        // Navigate to product page
        window.location.href = productUrl;
      });
    });
  }

  /**
   * Tracks product link clicks and handles navigation to product detail page.
   * Stores the selected variant ID in session storage for consistency with old implementation.
   *
   * @param link - The clicked link element
   * @param model - The recommendation model for context
   */
  protected trackProductLinkClick(link: HTMLAnchorElement, model: RecommendationModel): void {
    const href = link.getAttribute('href');
    const productHandle = this.extractProductIdFromHref(href);

    if (productHandle) {
      // Find the product in the model
      const product = model.properties.products.find((p) => {
        // Match by handle in the href
        return href?.includes(`/products/${p.handle}`);
      });

      if (product) {
        // Use the selected variant or first available variant
        const extendedProduct = product as ExtendedRecommendationProduct;
        const selectedVariant = extendedProduct.selectedOption.id;
        const firstVariant = product.variants[0];
        const variantId = selectedVariant || firstVariant?.id || product.id;

        // Store the selected product/variant ID in session storage
        // This matches the old SELECTED_PRODUCT_ID constant behavior
        try {
          sessionStorage.setItem('BOOST_SELECTED_PRODUCT_ID', String(variantId));
        } catch (error) {
          // Ignore sessionStorage errors in private browsing mode
          console.debug('Could not set sessionStorage:', error);
        }
      }
    }
  }

  /**
   * Extracts product handle from product URL href.
   *
   * @param href - The product URL (e.g., "/products/bronze-choker")
   * @returns Product handle or null if not found
   */
  protected extractProductIdFromHref(href: string | null): string | null {
    if (!href || !href.startsWith('/products/')) {
      return null;
    }

    // Extract product handle from URL like "/products/bronze-choker"
    const match = href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  /**
   * Updates the price display for a specific product item in the bundle widget.
   *
   * @param dropdownNodeRoot - The dropdown element containing the product
   * @param formattedPrice - The formatted price string to display
   */
  private updateItemPriceUI(dropdownNodeRoot: HTMLElement, formattedPrice: string): void {
    const itemInfoEl = dropdownNodeRoot.closest('.boost-sd__recommendation-bundle-item-info');

    const priceSpan = itemInfoEl?.querySelector('.boost-sd__format-currency span');

    if (priceSpan) {
      priceSpan.textContent = formattedPrice;

      const priceWrapper = priceSpan.closest('.boost-sd__format-currency');
      if (priceWrapper) {
        priceWrapper.setAttribute('aria-label', `Price: ${formattedPrice}`);
      }
    }
  }

  /**
   * Attaches click event handlers to carousel navigation buttons (Previous/Next).
   *
   * Binds click events to circular navigation buttons based on their aria-label
   * attributes. Override this method to customize button behavior, add additional
   * navigation controls, or implement custom navigation logic.
   *
   * @param container - The HTML element containing the carousel and buttons
   * @param slider - The initialized Slick carousel instance
   *
   * @example
   * Override to add keyboard shortcuts for navigation:
   * ```typescript
   * protected attachCarouselButtonHandlers(container, slider) {
   *   super.attachCarouselButtonHandlers(container, slider);
   *
   *   // Add keyboard navigation
   *   container.addEventListener('keydown', (e) => {
   *     if (e.key === 'ArrowLeft') {
   *       slider.slick('slickPrev');
   *     } else if (e.key === 'ArrowRight') {
   *       slider.slick('slickNext');
   *     }
   *   });
   * }
   * ```
   *
   * @example
   * Override to add analytics tracking to navigation:
   * ```typescript
   * protected attachCarouselButtonHandlers(container, slider) {
   *   const buttons = container.querySelectorAll('.boost-sd__button--circle');
   *   buttons.forEach((button) => {
   *     button.addEventListener('click', () => {
   *       const direction = button.getAttribute('aria-label');
   *       this.analytics.track('carousel_navigation', { direction });
   *
   *       if (direction === 'Next') {
   *         slider.slick('slickNext');
   *       } else if (direction === 'Previous') {
   *         slider.slick('slickPrev');
   *       }
   *     });
   *   });
   * }
   * ```
   */
  protected attachCarouselButtonHandlers(container: HTMLElement, slider: SlickInstance): void {
    const carouselButtons = container.querySelectorAll('.boost-sd__button--circle');
    carouselButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        const behavior = button.getAttribute('aria-label');
        if (behavior === 'Next') {
          slider.slick('slickNext');
        } else if (behavior === 'Previous') {
          slider.slick('slickPrev');
        }
      });
    });
  }

  /**
   * Renders the carousel widget by loading dependencies and initializing the carousel.
   *
   * This method handles the complete carousel rendering process:
   * 1. Lazy loads jQuery library with noConflict mode
   * 2. Lazy loads Slick carousel library
   * 3. Renders widget template with product data
   * 4. Initializes carousel with event handlers
   *
   * @param container - The HTML element where the carousel will be rendered
   *
   * @throws {Error} When jQuery/Slick loading fails, template rendering fails,
   * or carousel initialization encounters errors
   *
   * @remarks
   * Performance considerations:
   * - jQuery (~30KB gzipped) and Slick (~15KB gzipped) are loaded on-demand only for carousel layouts
   * - Uses noConflict mode to avoid conflicts with page's jQuery instance
   * - Template rendering is handled by AppService for consistency
   * - Total additional bundle size: ~45KB gzipped when carousel layout is used
   *
   * @example
   * Override to add custom carousel initialization logic:
   * ```typescript
   * async renderCarouselWidget(container) {
   *   // Add loading indicator
   *   container.innerHTML = '<div class="loading">Loading carousel...</div>';
   *
   *   await super.renderCarouselWidget(container);
   *
   *   // Add custom post-render effects
   *   this.addCarouselAnimations(container);
   * }
   * ```
   */
  async renderCarouselWidget(container: HTMLElement) {
    const jquery = await import('jquery');
    const $appJQuery = jquery.default.noConflict(true);
    window.$appJQuery = $appJQuery;

    await import('slick-carousel');

    const { model } = this.state.value;
    if (model && this.template) {
      const taeConfig = this.appService.TAEAppConfig;

      const html = await this.appService.templateRender(this.template, {
        ...model.properties,
        widgetDesignSettings: {
          ...model.properties.widgetDesignSettings,
          tenantId: taeConfig.shop?.domain,
        },
        request: {
          device: this.appService.detectDeviceByWidth(),
          money_format: taeConfig.shop?.money_format,
          money_format_with_currency: taeConfig.shop?.money_format_with_currency,
        },
      });

      container.innerHTML = html;
      this.applyRecommendationCarouselEvent(model, container);
    }
  }

  /**
   * Renders the bundle widget by processing product variants and initializing interactions.
   *
   * This method handles the complete bundle widget rendering process:
   * 1. Processes products and selects available variants
   * 2. Calculates initial total price and selection state
   * 3. Renders template with processed data
   * 4. Initializes bundle-specific event handlers
   *
   * @param container - The HTML element where the bundle widget will be rendered
   *
   * @throws {Error} When template rendering fails or model is unavailable
   *
   * @remarks
   * The method automatically selects the first available variant for each product
   * and marks products as checked only if they have available variants.
   *
   * @example
   * Override to add custom product filtering:
   * ```typescript
   * async renderBundleWidget(container) {
   *   // Custom pre-processing
   *   const { model } = this.state.value;
   *   if (model) {
   *     model.properties.products = this.filterProductsForShop(model.properties.products);
   *   }
   *
   *   await super.renderBundleWidget(container);
   * }
   * ```
   */
  async renderBundleWidget(container: HTMLElement): Promise<void> {
    const { model } = this.state.value;
    if (!model || !this.template) return;

    const modelProperties = model.properties as ExtendedRecommendationModelProperties;

    const products = modelProperties.products as ExtendedRecommendationProduct[];

    const updatedProducts = products.map((product) => {
      const firstAvailableVariant = product.variants.find((v) => v.available) ?? null;

      if (firstAvailableVariant) {
        const originPrice = Number(firstAvailableVariant.price) || 0;

        product.selectedOption = {
          title: firstAvailableVariant.title,
          originPrice,
          price: this.formatPrice(originPrice),
          id: firstAvailableVariant.id,
        };
        product.checked = true;
      } else {
        product.checked = false;
      }
      return product;
    });

    modelProperties.products = updatedProducts;
    modelProperties.totalPrice = this.formatPrice(this.calcTotalPrice(updatedProducts));
    modelProperties.hasSelectedProduct = updatedProducts.some((p) => p.checked);

    this.state.value = { ...this.state.value, model: model };

    this.state.value = { ...this.state.value, model: model };

    const taeConfig = this.appService.TAEAppConfig;
    const html = await this.appService.templateRender(this.template, {
      ...modelProperties,
      widgetDesignSettings: {
        ...modelProperties.widgetDesignSettings,
        tenantId: taeConfig.shop?.domain,
      },
      request: {
        device: this.appService.detectDeviceByWidth(),
        money_format: taeConfig.shop?.money_format,
        money_format_with_currency: taeConfig.shop?.money_format_with_currency,
      },
    });

    container.innerHTML = html;

    this.applyRecommendationBundleEvent(model, container);
  }
}

/**
 * Configuration properties for initializing the RecommendationWidgetController.
 */
export type RecommendationWidgetControllerProps = {
  /**
   * The unique identifier of the widget to initialize.
   * This ID is used to fetch widget configuration and recommendation data from the API.
   */
  widgetId: string;

  /**
   * Skip page validation when fetching recommendation data.
   * Useful for cart drawer where widgets should render regardless of current page.
   */
  skipValidation?: boolean;

  /**
   * Render the widget immediately without waiting for viewport intersection.
   * Useful for modals/drawers where IntersectionObserver may not work correctly.
   * @default false
   */
  renderImmediately?: boolean;
};

/**
 * Internal state management for the recommendation widget controller.
 */
type RecommendationWidgetControllerState = {
  /**
   * Indicates whether the widget is currently fetching recommendation data.
   */
  loading: boolean;

  /**
   * The recommendation model containing widget configuration and product data.
   * Null when data hasn't been fetched yet or when fetch fails.
   */
  model: RecommendationModel | null;
};

/**
 * Slick carousel configuration options.
 *
 * Defines the behavior and appearance of the Slick carousel used for
 * recommendation widgets with carousel layout. For complete options reference,
 * see: https://kenwheeler.github.io/slick/
 */
interface SlickOptions {
  /** Whether to show dot indicators for navigation */
  dots?: boolean;

  /** Whether the carousel should loop infinitely */
  infinite?: boolean;

  /** Number of slides to show at once */
  slidesToShow?: number;

  /** Number of slides to scroll at once */
  slidesToScroll?: number;

  /** Whether to show previous/next arrow buttons */
  arrows?: boolean;

  /** Whether the carousel can be dragged with mouse */
  draggable?: boolean;

  /** Whether to use CSS3 transforms for slide transitions */
  useTransform?: boolean;

  /** Whether clicking a slide focuses it */
  focusOnSelect?: boolean;

  /** Whether to focus slides when they change */
  focusOnChange?: boolean;

  /** Whether to enable accessibility keyboard navigation */
  accessibility?: boolean;

  /** Responsive breakpoint configurations */
  responsive?: Array<{
    /** The breakpoint width in pixels */
    breakpoint: number;
    /** Settings to apply when below this breakpoint */
    settings: Partial<SlickOptions>;
  }>;

  /** Whether to enable right-to-left layout direction */
  rtl?: boolean;
}

/**
 * Slick carousel instance interface for type-safe method calls.
 *
 * Provides type definitions for interacting with an initialized Slick carousel.
 * Represents the jQuery plugin instance returned by $('.element').slick().
 */
interface SlickInstance {
  /**
   * Executes a Slick carousel method.
   *
   * @param options - Either a configuration object for initialization, or a method name string
   * (e.g., 'slickNext', 'slickPrev', 'slickGoTo')
   * @param args - Additional arguments for the specified method (e.g., slide index for 'slickGoTo')
   * @returns The Slick instance for method chaining
   */
  slick(options: SlickOptions | string, ...args: unknown[]): SlickInstance;

  /**
   * Attaches an event handler to the carousel.
   *
   * @param event - The event name (e.g., 'afterChange', 'beforeChange')
   * @param handler - The event handler function to execute
   */
  on(event: string, handler: () => void): void;
}
